import React, {useEffect, /* useMemo, */ useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Text, useMultiStyleConfig, Flex, useDisclosure} from '@chakra-ui/react'
import Checkbox from '../../checkbox'
import {FormattedNumber, useIntl, FormattedMessage} from 'react-intl'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    useShopperBasketsMutation,
    useShopperBasketsMutationHelper,
} from '@salesforce/commerce-sdk-react'
import Dropdown from '../../dropdown'
import {buildWarrantyOption, getWarrantyDisplayDescription} from '../../../utils/product-utils'
import WarrantyCareModal from '../../modals/warranty-care-modal'
import {
    useCustomPreferenceWithFallback,
    useCustomPreferenceWithLocale,
} from '../../../hooks/use-custom-preference'

const WarrantySectionCart = ({warranties, product}) => {
    const styles = useMultiStyleConfig('WarrantySectionCart', {})
    const {data: basket} = useCurrentBasket()
    const {formatMessage} = useIntl()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const removeItemFromBasket = useShopperBasketsMutation('removeItemFromBasket')
    const actualWarranty = basket?.productItems?.find(
        (li) => li?.c_UUIDWarranty === product?.c_UUIDWarranty && li?.c_isWarranty === true,
    )
    const actualWarrantyId = actualWarranty?.productId
    const [selectedWarrantyId, setSelectedWarrantyId] = useState(actualWarrantyId || null)
    const [isBusy, setIsBusy] = useState(false)
    const [isChecked, setIsChecked] = useState(false)
    const {isOpen, onOpen, onClose} = useDisclosure()

    const addWarranty = async (warrantySkuId) => {
        const parentProductId = product?.productId || product?.id
        const item = {
            productId: warrantySkuId,
            quantity: 1,
            c_isWarranty: true,
            c_parentProductId: parentProductId,
            c_UUIDWarranty: product?.c_UUIDWarranty,
        }
        await addItemToNewOrExistingBasket([item])
    }

    const hasBonusWarranty = !!(
        product?.c_UUIDWarranty &&
        basket?.productItems?.some(
            (li) =>
                li?.c_isWarranty === true &&
                li?.bonusProductLineItem === true &&
                li?.c_UUIDWarranty === product.c_UUIDWarranty,
        )
    )

    const isLocked = hasBonusWarranty

    const removeWarranty = async () => {
        if (!actualWarranty?.itemId || !basket?.basketId) return
        await removeItemFromBasket.mutateAsync({
            parameters: {basketId: basket.basketId, itemId: actualWarranty.itemId},
        })
    }

    const updateWarranty = async (newWarrantySkuId) => {
        if (!actualWarranty?.itemId || !basket?.basketId || !product?.c_UUIDWarranty) return

        const item = {
            productId: newWarrantySkuId,
            quantity: 1,
            c_isWarranty: true,
            c_UUIDWarranty: product.c_UUIDWarranty,
        }

        await updateItemInBasketMutation.mutateAsync({
            parameters: {basketId: basket.basketId, itemId: actualWarranty.itemId},
            body: item,
        })
    }

    // Toggle single-select
    const onToggle = async (warrantySkuId, selected) => {
        if (isBusy || isLocked) return
        setIsBusy(true)
        try {
            if (!selected && selectedWarrantyId === warrantySkuId) {
                await removeWarranty()
                setSelectedWarrantyId(null)
                return
            }

            if (actualWarrantyId && actualWarrantyId !== warrantySkuId) {
                await updateWarranty(warrantySkuId)
            } else if (!actualWarrantyId) {
                await addWarranty(warrantySkuId)
            }

            setSelectedWarrantyId(warrantySkuId)
        } finally {
            setIsBusy(false)
        }
    }

    useEffect(() => {
        setSelectedWarrantyId(actualWarrantyId || null)
    }, [actualWarrantyId, basket?.productItems])

    useEffect(() => {
        if (actualWarrantyId) {
            setIsChecked(true)
        }
    }, [actualWarrantyId])

    const warrantyTermsAndConditionsLinkLocale = useCustomPreferenceWithLocale(
        'warrantyTermsAndConditionsLink',
    )
    const warrantyTermsAndConditionsLinkFallback = useCustomPreferenceWithFallback(
        'warrantyTermsAndConditionsLink',
    )
    const warrantyTermsAndConditionsLink =
        warrantyTermsAndConditionsLinkLocale || warrantyTermsAndConditionsLinkFallback

    // Only show if there are warranties available
    if (product?.c_isWarranty || !warranties || warranties?.length === 0) {
        return null
    }

    return (
        <>
            <Flex {...styles.outterContainer}>
                <Stack {...styles.outterWarrantyItemContainer}>
                    {warranties.map((warranty) => {
                        const selectedOption = warranty.options.find(
                            (opt) =>
                                selectedWarrantyId === opt.productId ||
                                selectedWarrantyId === opt.id,
                        )
                        const selectedPrice = selectedOption?.price || selectedOption?.currentPrice
                        const dropdownOptions = warranty.options.map((opt) => ({
                            label: buildWarrantyOption(opt.serviceDuration, formatMessage),
                            value: opt.productId || opt.id,
                        }))
                        const dropdownValue =
                            dropdownOptions.find((o) => o.value === selectedWarrantyId) || null
                        const displayDescription = getWarrantyDisplayDescription(
                            warranty.description,
                        )
                        const handleCheckboxToggle = () => {
                            if (isChecked && selectedWarrantyId) {
                                onToggle(selectedWarrantyId, false)
                            } else if (!isChecked && !selectedWarrantyId && dropdownOptions[0]) {
                                onToggle(dropdownOptions[0].value, true)
                            }
                            setIsChecked(!isChecked)
                        }
                        return (
                            <Stack
                                key={warranty.groupName}
                                {...styles.itemContainer}
                                {...(isChecked && styles.itemContainerChecked)}
                                cursor={isLocked || isBusy ? 'default' : 'pointer'}
                                onClick={() => {
                                    if (isLocked || isBusy) return
                                    handleCheckboxToggle()
                                }}
                            >
                                <Flex {...styles.warrantyGroupHeaderContainer}>
                                    <Flex {...styles.warrantyGroupNameCheckboxContainer}>
                                        <Text {...styles.warrantyGroupName}>
                                            {warranty.groupName}
                                        </Text>

                                        <Box onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                isChecked={isChecked}
                                                isDisabled={isLocked || isBusy}
                                                onChange={handleCheckboxToggle}
                                            />
                                        </Box>
                                    </Flex>

                                    <Flex {...styles.warrantyGroupSubtitleContainer}>
                                        <Flex gap={1} alignItems="baseline" wrap="wrap">
                                            {displayDescription && (
                                                <Text
                                                    textStyle="body-small"
                                                    color="foreground.neutral.muted"
                                                    fontWeight="normal"
                                                    noOfLines="2"
                                                >
                                                    {displayDescription}
                                                </Text>
                                            )}
                                            <Text
                                                {...styles.warrantyGroupLinkButton}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    onOpen()
                                                }}
                                            >
                                                <FormattedMessage
                                                    id="product_view.care.learn_more"
                                                    defaultMessage="Learn more"
                                                />
                                            </Text>
                                        </Flex>
                                        <WarrantyCareModal
                                            isOpen={isOpen}
                                            onClose={onClose}
                                            exploreLink={warrantyTermsAndConditionsLink}
                                        />
                                    </Flex>
                                </Flex>

                                {isChecked && (
                                    <Flex
                                        {...styles.warrantyOptionsContainer}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Dropdown
                                            name={`warranty-${warranty.groupName}`}
                                            label={
                                                <FormattedMessage
                                                    id="product_view.warranty.dropdown_label"
                                                    defaultMessage="Select duration"
                                                />
                                            }
                                            onChange={(option) =>
                                                option && onToggle(option.value, true)
                                            }
                                            value={dropdownValue}
                                            options={dropdownOptions}
                                            isDisabled={isLocked || isBusy}
                                            size="xl"
                                            {...styles.dropdown}
                                            variant="warantySelection"
                                        />

                                        {selectedPrice != null && (
                                            <Text {...styles.price}>
                                                <FormattedNumber
                                                    value={selectedPrice}
                                                    currency={basket?.currency}
                                                    style="currency"
                                                />
                                            </Text>
                                        )}
                                    </Flex>
                                )}
                            </Stack>
                        )
                    })}
                </Stack>
            </Flex>
        </>
    )
}

WarrantySectionCart.propTypes = {
    warranties: PropTypes.array.isRequired,
    product: PropTypes.object.isRequired,
    warrantyTermsAndConditionsLink: PropTypes.string,
}

export default WarrantySectionCart
